// Department (L1) selector for the dashboard filter bar (#63 follow-up). Until
// now the only way into the category taxonomy was the discovery tiles on the
// empty state — once a search/filter was active there was no category control
// at all. This dropdown is a first-class peer of the Brand/Retailer filters:
// picking a department sets the active category (the server expands it to the
// full subtree), after which the breadcrumb + L2/L3 chip bar handle deeper
// drill-down. Single-select, since product search takes one category.
//
// Visuals follow the design system (docs/design/component-catalogue.md §Badges/
// Chips): active fill = `bg-emerald-500 text-dark`; mirrors the inline Brand/
// Retailer dropdowns so the filter row reads as one consistent group.
import React, { useEffect, useRef, useState } from 'react';
import type { Category } from '../shared/types';
import { l1Departments, emojiForDepartment } from '../services/category-nav';
import { useLanguage } from '../services/LanguageContext';

interface Props {
  categories: Category[];
  activeCategoryId: string; // 'All' or a category id (possibly a drilled L2/L3)
  onSelect: (departmentId: string) => void; // pick a department
  onClear: () => void; // back to 'All'
}

const CategoryFilterDropdown: React.FC<Props> = ({ categories, activeCategoryId, onSelect, onClear }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const departments = l1Departments(categories);
  const isActive = activeCategoryId !== 'All';

  // Close on outside-click or Escape (a11y — keyboard users can dismiss).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // The button reflects the active category's own name (may be a drilled L2/L3),
  // falling back to "All categories" when nothing is selected.
  const buttonLabel = isActive
    ? (categories.find(c => c.id === activeCategoryId)?.name ?? t('filter.allCategories'))
    : t('filter.allCategories');

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[10px] font-black uppercase text-slate-400 mr-2">{t('filter.category')}:</span>
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t('filter.category')}
          className={`px-4 py-1.5 rounded-lg font-bold text-xs transition-all flex items-center gap-2 ${isActive ? 'bg-emerald-500 text-dark' : 'bg-slate-50 dark:bg-dark-border/30 text-slate-500 dark:text-emerald-500/40 hover:bg-emerald-500/5'}`}
        >
          {buttonLabel}
          <i className={`fas fa-chevron-down text-[9px] transition-transform ${open ? 'rotate-180' : ''}`}></i>
        </button>
        {open && (
          <div role="listbox" aria-label={t('filter.category')} className="absolute z-20 top-full left-0 mt-1 w-56 bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-xl shadow-xl max-h-64 overflow-y-auto">
            <button
              type="button"
              role="option"
              aria-selected={!isActive}
              onClick={() => { onClear(); setOpen(false); }}
              className={`w-full px-4 py-2 text-left text-xs font-bold flex items-center gap-2 border-b border-slate-100 dark:border-dark-border ${!isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-dark-border/30'}`}
            >
              <i className="fas fa-layer-group w-4 text-center"></i>{t('filter.allCategories')}
            </button>
            {departments.length === 0 && (
              <p className="px-4 py-3 text-xs font-bold text-slate-400">{t('filter.allCategories')}</p>
            )}
            {departments.map(d => {
              const selected = activeCategoryId === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => { onSelect(d.id); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-left ${selected ? 'bg-emerald-500/10' : 'hover:bg-slate-50 dark:hover:bg-dark-border/30'}`}
                >
                  <span className="text-base leading-none" aria-hidden>{emojiForDepartment(d.name)}</span>
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{d.name}</span>
                  {selected && <i className="fas fa-check text-emerald-500 text-[10px] ml-auto"></i>}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {isActive && (
        <button
          type="button"
          onClick={onClear}
          className="px-3 py-1.5 rounded-lg font-bold text-xs transition-all bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 flex items-center gap-1"
        >
          <i className="fas fa-times text-[10px]"></i>{t('filter.clearFilter')}
        </button>
      )}
    </div>
  );
};

export default CategoryFilterDropdown;
